import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import BuyGuide from "../nintendoDeals/BuyGuide";
import CardsCheckout from "../nintendoDeals/CardsCheckout";
import { PS_STORE_ID, bestPsPlan } from "./psCards";

function money(value, currency) {
  try {
    return new Intl.NumberFormat("ru-RU", { style: "currency", currency }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

const faceLabel = (value, currency) => money(value, currency).replace(/[,.]00(?=\D*$)/, "");
const rub = (value) => `${Math.round(value).toLocaleString("ru-RU")} ₽`;
const dateRu = (value) =>
  new Date(value).toLocaleDateString("ru", { day: "numeric", month: "long" });
const parseAmount = (text) =>
  Number(String(text).replace(",", ".").replace(/[^\d.]/g, "")) || 0;
const roundCents = (value) => Math.round(value * 100) / 100;

function plural(n, one, few, many) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

/** Игра из витрины скидок PS5: подбор карт PSN под цену и оформление заказа. */
const PsPickedGame = forwardRef(function PsPickedGame(
  { pick, catalogs, cardsError, onRefreshCards, onClose },
  ref,
) {
  const { game, region, currency, label } = pick;
  const [balance, setBalance] = useState("");
  const [buyOpen, setBuyOpen] = useState(false);

  useEffect(() => {
    setBalance("");
    setBuyOpen(false);
  }, [game.id, region]);

  const balanceValue = parseAmount(balance);
  const need = Math.max(0, roundCents(game.price - balanceValue));
  const best = useMemo(
    () => (need > 0 ? bestPsPlan(need, catalogs) : null),
    [need, catalogs],
  );
  const plan = best?.plan;
  const left = plan ? roundCents(balanceValue + plan.face - game.price) : 0;
  const codesLabel = plan ? `${plan.codes} ${plural(plan.codes, "код", "кода", "кодов")}` : "";
  const storeRegion = best?.catalog.regionId || catalogs[0]?.regionId;

  return (
    <div
      ref={ref}
      className="relative mx-auto mt-4 max-w-3xl scroll-mt-24 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#2a2a2a]"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Закрыть"
        className="absolute right-2 top-2 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>

      <div className="flex gap-4 pr-6">
        {game.image ? (
          <img
            src={game.image}
            alt=""
            className="h-24 w-24 shrink-0 rounded bg-gray-100 object-cover sm:h-28 sm:w-28"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold leading-snug text-gray-900 dark:text-white">
            {game.title}
          </div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-1.5 text-base">
            <b className="text-gray-900 dark:text-white">{money(game.price, currency)}</b>
            {game.original != null && game.original > game.price ? (
              <>
                <span className="text-xs text-gray-400 line-through">
                  {money(game.original, currency)}
                </span>
                {game.percent ? (
                  <span className="text-xs font-semibold text-red-600">−{game.percent}%</span>
                ) : null}
              </>
            ) : null}
          </div>
          {game.endsAt ? (
            <div className="mt-1 text-xs text-red-600">скидка до {dateRu(game.endsAt)}</div>
          ) : null}
          {game.storeUrl ? (
            <a
              href={game.storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block text-xs text-indigo-600 hover:underline dark:text-indigo-400"
            >
              открыть в PlayStation Store →
            </a>
          ) : null}
        </div>
      </div>

      <div className="mt-4 border-t border-gray-200 pt-3 text-sm dark:border-gray-700">
        {need === 0 ? (
          <div className="rounded-lg bg-green-50 px-3 py-2 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            Баланса хватает — карты не нужны
          </div>
        ) : plan ? (
          <>
            <div className="text-gray-600 dark:text-gray-300">
              {balanceValue > 0 ? `Не хватает ${money(need, currency)}. ` : ""}
              Подойдут карты PSN {label}:
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {plan.items.map((item) => (
                <span
                  key={item.id}
                  className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 dark:border-indigo-700 dark:bg-indigo-900/40"
                >
                  <b>{faceLabel(item.face, currency)}</b>
                  {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                </span>
              ))}
              <span className="font-semibold">= {rub(plan.rub)}</span>
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {left > 0
                ? `После покупки на балансе останется ${money(left, currency)}`
                : "Сумма совпадает с ценой игры без остатка"}
            </div>
          </>
        ) : !catalogs.length ? (
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
            {cardsError
              ? "Каталог карт сейчас недоступен — не получилось подобрать карты. Попробуйте обновить страницу чуть позже."
              : "Загружаем карты…"}
          </div>
        ) : (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            Не хватает карт в наличии на {money(need, currency)}
          </div>
        )}

        <label className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          Мой баланс PSN:
          <span className="inline-flex items-center gap-1">
            <input
              inputMode="decimal"
              value={balance}
              placeholder="0"
              onChange={(e) => setBalance(e.target.value.replace(/[^\d.,]/g, ""))}
              className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-800 outline-none focus:border-indigo-500 dark:border-gray-600 dark:bg-[#222222] dark:text-gray-100"
            />
            {currency}
          </span>
          <span className="text-gray-400">необязательно, для точного расчёта</span>
        </label>

        {region === "us" ? (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Цены PlayStation Store США указаны без налога. Если в аккаунте указан штат с
            налогом с продаж, PlayStation добавит его при оплате — проверьте итог в корзине.
          </p>
        ) : null}

        {plan ? (
          <>
            <button
              type="button"
              onClick={() => setBuyOpen((current) => !current)}
              className="mt-3 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              {buyOpen ? "Скрыть оформление" : `Купить ${codesLabel} за ${rub(plan.rub)}`}
            </button>
            {buyOpen ? (
              <CardsCheckout plan={plan} onPriceChanged={onRefreshCards} storeId={PS_STORE_ID} />
            ) : null}
          </>
        ) : need > 0 ? (
          <Link
            to={`/store/playstation${storeRegion ? `?region=${storeRegion}` : ""}`}
            className="mt-3 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            Купить карты PSN {label}
          </Link>
        ) : null}
        <BuyGuide label={label} storeId={PS_STORE_ID} />
      </div>
    </div>
  );
});

export default PsPickedGame;
